import { Box, ResponsiveContext } from "grommet";
import React, { useState, useContext } from "react";
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import AppLayout from "../../components/layouts/AppLayout";
import CustomerList from "../../components/pages/customers/CustomerList";
import SubMenu from "../../components/SubMenu";
import {
    IconPlus,
    IconChevronDown as IconArrowDown
} from '@tabler/icons-react';
import CustomerImportMenu from "../../components/pages/customers/CustomerImportMenu";
import AddCustomerDrawer from "../../components/pages/customers/AddCustomerDrawer";
import CustomerGroupFormDrawer from "../../components/pages/customers/CustomerGroupFormDrawer";
import { useRouter } from "next/router";
import { handleErrors, silentFetcher } from "../../utils/errors";
import { bulkMutate } from "../../utils/cache";
import useSWR from "swr";
import Button from "../../components/Button";
import BouncedEmailTable from "../../components/pages/customers/BouncedEmailTable";
import KeyboardShortcut from "../../components/KeyboardShortcut";
import { MainButtonWithExtension, ExtensionButton } from "../../utils/styled";
import useKeyboardShortcut from "../../utils/hooks/useKeyboardShortcut";

const CustomerPage = (props) => {
    const {isGroup} = props;
    const [showAddDrawer, setShowAddDrawer] = useState(false);
    const [showAddGroupDrawer, setShowAddGroupDrawer] = useState(false);
    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    const fetcher = (url) => fetch(url)
        .then(handleErrors)
        .catch(props.showNotification);

    const router = useRouter();
    const { action } = router.query;

    // Keyboard shortcut 'n' to open add drawer
    useKeyboardShortcut('n', () => {
        if (isGroup) {
            setShowAddGroupDrawer(true);
        } else {
            setShowAddDrawer(true);
        }
    });
    const {data: summary} = useSWR(`/api/company/customer-summary`, fetcher, { revalidateOnFocus: false });
    const CustomerTabMenu = [
        { label: 'All', href: '/customers' },
        { label: 'Groups', href: '/customers/groups'  },
        { label: `Applications${summary && summary?.total_applications > 0 ? ` (${summary?.total_applications})` : ''}`, href: '/customers/applications'  }
    ];

    // only fetch bounced email when we're on 'all' tab
    const {data: bouncedEmailData, error: bouncedEmailError} = useSWR(
        !isGroup && !props.children ? `/api/company/bounced_emails` : undefined,
        silentFetcher,
        {
            revalidateOnFocus: false,
        }
    );

    return (
        <AppLayout
            title="Customers"
            hasStickyDesktopPageHeader
            pageControls={isGroup ? (
                <Button
                    event={{section: 'customer_page_header', element: 'add_group'}}
                    fill={isMobile ? "horizontal" : undefined}
                    icon={(<FontAwesomeIcon SelectedIcon={IconPlus} size="xxsmall" />)}
                    label={(
                        <Box direction="row" gap="xsmall" align="center" fill={isMobile ? "horizontal" : undefined}>
                            Add Group
                            {!isMobile && <KeyboardShortcut keys="N" />}
                        </Box>
                    )}
                    responsive={false}
                    secondary
                    onClick={() => setShowAddGroupDrawer(true)}
                />
            ) : (
                <Box direction="row" fill={isMobile ? "horizontal" : undefined}>
                    <MainButtonWithExtension
                        event={{section: 'customer_page_header', element: 'add_customer'}}
                        fill={isMobile ? "horizontal" : undefined}
                        button
                        label={(
                            <Box direction="row" gap="xsmall" align="center" fill={isMobile ? "horizontal" : undefined}>
                                Add Customer
                                {!isMobile && <KeyboardShortcut keys="N" />}
                            </Box>
                        )}
                        icon={<FontAwesomeIcon SelectedIcon={IconPlus} size="xxsmall" />}
                        secondary
                        hideRightBorder
                        onClick={() => setShowAddDrawer(true)}
                    />
                    <ExtensionButton
                        event={{section: 'customer_page_header', element: 'import_extension'}}
                        responsive={false}
                        dropProps={{
                            align: { top: 'bottom', right: 'right' }
                        }}
                        secondary
                        icon={(<FontAwesomeIcon SelectedIcon={IconArrowDown} size="xxsmall" />)}
                        dropContent={
                            <CustomerImportMenu onAddGroup={() => setShowAddGroupDrawer(true)} />
                        }
                    />
                </Box>
            )}>
            <SubMenu items={CustomerTabMenu} margin={{top: 'small'}}/>
            { bouncedEmailData && bouncedEmailData.total >= 1 && (
                <BouncedEmailTable bouncedEmailData={bouncedEmailData} />
            )}
            {props?.children || (
                <CustomerList 
                    isGroup={isGroup}
                    dataName={'customers'}
                    totalCount={isGroup ? summary?.total_groups : summary?.total_customers}
                    tab={isGroup ? "groups" : 'individuals'}
                    dataTableWrapperProps={{margin: "none"}} />
            )}
            {(showAddDrawer || action === "add") && (
                <AddCustomerDrawer
                    onSuccess={({ id, uuid }) => {
                        bulkMutate("/api/customers");
                        setShowAddDrawer(false);
                        router.replace(`/customers/${uuid || id || ""}`);
                    }}
                    onClose={() => {
                        setShowAddDrawer(false);
                        if (action === "add") {
                            router.back();
                        }
                    }}
                />
            )}
            {(showAddGroupDrawer || action === "add-group") && (
                <CustomerGroupFormDrawer
                    action="add"
                    onSuccess={({ uuid }) => {
                        setShowAddGroupDrawer(false);
                        router.replace(`/customers/groups/${uuid || ""}`);
                    }}
                    onClose={() => {
                        setShowAddGroupDrawer(false);
                        if (action === "add-group") {
                            router.back();
                        }
                    }}
                />
            )}
        </AppLayout>
    )
}

export default CustomerPage;
